import { supabase } from './supabase';
import type { PromoCode } from './promoCodes';

/**
 * Accès Supabase pour les codes promo :
 *  - CLIENT (vitrine) : aperçu via la RPC `validate_promo_code` (aucune écriture,
 *    ne lit jamais la table des codes en direct → codes non exposés).
 *  - ADMIN (resto) : CRUD des codes + compteurs d'utilisation (RLS resto/super-admin).
 * La logique métier des règles reste dans promoCodes.ts (pur, testé).
 */

export interface PromoPreview {
  valid: boolean;
  discountCents: number;
  finalCents: number;
  /** Motif serveur si invalide (inactive/expired/min_order/already_used/…). */
  reason: string | null;
}

/** Aperçu serveur d'un code au checkout (montant de réduction sans le consommer). */
export async function previewPromoCode(
  tenantId: string,
  code: string,
  subtotalCents: number,
  phone: string | null | undefined,
): Promise<PromoPreview> {
  const { data, error } = await supabase.rpc('validate_promo_code', {
    p_tenant_id: tenantId,
    p_code: code,
    p_subtotal_cents: subtotalCents,
    p_phone: phone ?? '',
  });
  if (error) {
    return { valid: false, discountCents: 0, finalCents: subtotalCents, reason: 'inactive' };
  }
  const d = (data ?? {}) as { valid?: boolean; discount_cents?: number; final_cents?: number; reason?: string | null };
  return {
    valid: !!d.valid,
    discountCents: d.discount_cents ?? 0,
    finalCents: d.final_cents ?? subtotalCents,
    reason: d.reason ?? null,
  };
}

// ------------------------------- ADMIN -------------------------------

export interface PromoCodeWithUsage extends PromoCode {
  created_at?: string | null;
  redemptions?: number;
}

const PROMO_COLUMNS =
  'id, tenant_id, code, type, value, min_order_cents, max_discount_cents, first_order_only, starts_at, ends_at, max_redemptions, active, created_at';

/** Liste les codes d'un resto + leur nombre d'utilisations. */
export async function listPromoCodes(tenantId: string): Promise<PromoCodeWithUsage[]> {
  const { data, error } = await supabase
    .from('promo_codes')
    .select(PROMO_COLUMNS)
    .eq('tenant_id', tenantId)
    .order('created_at', { ascending: false });
  if (error) {
    console.warn('[promo] liste échouée:', error.message);
    return [];
  }
  const codes = (data as unknown as PromoCodeWithUsage[]) ?? [];
  const ids = codes.map((c) => c.id);
  if (ids.length) {
    const { data: reds } = await supabase.from('promo_redemptions').select('promo_code_id').in('promo_code_id', ids);
    const counts: Record<string, number> = {};
    for (const r of ((reds as { promo_code_id: string }[]) ?? [])) counts[r.promo_code_id] = (counts[r.promo_code_id] ?? 0) + 1;
    for (const c of codes) c.redemptions = counts[c.id] ?? 0;
  }
  return codes;
}

export interface PromoCodeInput {
  code: string;
  type: 'percent' | 'amount';
  value: number;
  min_order_cents?: number | null;
  max_discount_cents?: number | null;
  first_order_only?: boolean;
  starts_at?: string | null;
  ends_at?: string | null;
  max_redemptions?: number | null;
  active?: boolean;
}

/** Crée un code promo pour un resto. */
export async function createPromoCode(tenantId: string, input: PromoCodeInput): Promise<PromoCode | null> {
  const { data, error } = await supabase
    .from('promo_codes')
    .insert({ tenant_id: tenantId, ...input, code: input.code.trim().toUpperCase() })
    .select(PROMO_COLUMNS)
    .single();
  if (error) {
    console.warn('[promo] création échouée:', error.message);
    return null;
  }
  return data as PromoCode;
}

/** Met à jour un code (ex. activer/désactiver, changer les dates). */
export async function updatePromoCode(id: string, patch: Partial<PromoCodeInput>): Promise<boolean> {
  const clean = { ...patch };
  if (clean.code) clean.code = clean.code.trim().toUpperCase();
  const { error } = await supabase.from('promo_codes').update(clean).eq('id', id);
  if (error) {
    console.warn('[promo] maj échouée:', error.message);
    return false;
  }
  return true;
}

/** Active / désactive un code (codes rotatifs). */
export async function setPromoCodeActive(id: string, active: boolean): Promise<boolean> {
  return updatePromoCode(id, { active });
}

/** Supprime un code (les utilisations passées restent, via ON DELETE CASCADE côté DB). */
export async function deletePromoCode(id: string): Promise<boolean> {
  const { error } = await supabase.from('promo_codes').delete().eq('id', id);
  if (error) {
    console.warn('[promo] suppression échouée:', error.message);
    return false;
  }
  return true;
}
